/**
 * Content Classification TypeScript Types (CCE-005)
 * 
 * Mirrors backend/app/models/content_classification.py exactly.
 * 9-facet classification schema with 95+ enumerated values.
 * 
 * CRITICAL: Field names use snake_case to match Python API responses.
 * DO NOT convert to camelCase - API contract requires exact match.
 * 
 * Created: February 3, 2026
 * Epic: CCE - Content Classification Enhancement
 */

// =============================================================================
// FACET 1: FormatType (16 values)
// =============================================================================
export type FormatType =
  | 'FMT_WHITEPAPER'
  | 'FMT_CASE_STUDY'
  | 'FMT_DATASHEET'
  | 'FMT_BLOG'
  | 'FMT_EBOOK'
  | 'FMT_INFOGRAPHIC'
  | 'FMT_VIDEO'
  | 'FMT_WEBINAR'
  | 'FMT_PODCAST'
  | 'FMT_PRESENTATION'
  | 'FMT_ONE_PAGER'
  | 'FMT_EMAIL'
  | 'FMT_SOCIAL'
  | 'FMT_LANDING_PAGE'
  | 'FMT_PROPOSAL'
  | 'FMT_OTHER';

// =============================================================================
// FACET 2: PurposeType (7 values)
// =============================================================================
export type PurposeType =
  | 'PUR_EDUCATE'
  | 'PUR_PERSUADE'
  | 'PUR_ENABLE'
  | 'PUR_COMPARE'
  | 'PUR_VALIDATE'
  | 'PUR_ONBOARD'
  | 'PUR_RETAIN';

// =============================================================================
// FACET 3: AudienceLevel (5 values)
// =============================================================================
export type AudienceLevel =
  | 'AUD_EXECUTIVE'
  | 'AUD_MANAGER'
  | 'AUD_PRACTITIONER'
  | 'AUD_TECHNICAL'
  | 'AUD_END_USER';

// =============================================================================
// FACET 4: AudienceDomain (13 values)
// =============================================================================
export type AudienceDomain =
  | 'DOM_IT'
  | 'DOM_SECURITY'
  | 'DOM_FINANCE'
  | 'DOM_MARKETING'
  | 'DOM_SALES'
  | 'DOM_HR'
  | 'DOM_OPERATIONS'
  | 'DOM_LEGAL'
  | 'DOM_PROCUREMENT'
  | 'DOM_ENGINEERING'
  | 'DOM_PRODUCT'
  | 'DOM_CUSTOMER_SUCCESS'
  | 'DOM_GENERAL';

// =============================================================================
// FACET 5: IndustryType (16 values)
// =============================================================================
export type IndustryType =
  | 'IND_TECHNOLOGY'
  | 'IND_FINANCIAL'
  | 'IND_HEALTHCARE'
  | 'IND_MANUFACTURING'
  | 'IND_RETAIL'
  | 'IND_GOVERNMENT'
  | 'IND_EDUCATION'
  | 'IND_MEDIA'
  | 'IND_TELECOM'
  | 'IND_ENERGY'
  | 'IND_TRANSPORTATION'
  | 'IND_HOSPITALITY'
  | 'IND_REAL_ESTATE'
  | 'IND_NONPROFIT'
  | 'IND_PROFESSIONAL_SERVICES'
  | 'IND_CROSS_INDUSTRY';

// =============================================================================
// FACET 6: TechnicalDepth (4 values)
// =============================================================================
export type TechnicalDepth =
  | 'TECH_EXECUTIVE'
  | 'TECH_BUSINESS'
  | 'TECH_TECHNICAL'
  | 'TECH_DEEP_DIVE';

// =============================================================================
// FACET 7: EntityType (4 values) - 9th Facet
// =============================================================================
export type EntityType =
  | 'ENT_CLIENT'
  | 'ENT_PARTNER'
  | 'ENT_VENDOR'
  | 'ENT_SOLUTION';

// =============================================================================
// FACET 8: SubjectDiscipline (23 values)
// =============================================================================
export type SubjectDiscipline =
  | 'DISC_CLOUD_INFRASTRUCTURE'
  | 'DISC_CYBERSECURITY'
  | 'DISC_DATA_ANALYTICS'
  | 'DISC_AI_ML'
  | 'DISC_DEVOPS'
  | 'DISC_NETWORKING'
  | 'DISC_STORAGE'
  | 'DISC_VIRTUALIZATION'
  | 'DISC_IDENTITY_ACCESS'
  | 'DISC_COMPLIANCE_GOVERNANCE'
  | 'DISC_BUSINESS_CONTINUITY'
  | 'DISC_DIGITAL_TRANSFORMATION'
  | 'DISC_CUSTOMER_EXPERIENCE'
  | 'DISC_SALES_ENABLEMENT'
  | 'DISC_MARKETING_AUTOMATION'
  | 'DISC_FINANCIAL_MANAGEMENT'
  | 'DISC_HR_MANAGEMENT'
  | 'DISC_SUPPLY_CHAIN'
  | 'DISC_PROJECT_MANAGEMENT'
  | 'DISC_COLLABORATION'
  | 'DISC_MOBILE_SOLUTIONS'
  | 'DISC_IOT'
  | 'DISC_GENERAL';

// =============================================================================
// FACET 9: JourneyStage (imported from unified_stages.py via API)
// =============================================================================
// Note: buyer_stage is string type in API to accommodate 9 JourneyStage values
export type BuyerStage = string; // 'awareness' | 'consideration' | ... | 'retention'

// =============================================================================
// Additional Classification Metadata
// =============================================================================
export type ContentVisibility =
  | 'VIS_INTERNAL'
  | 'VIS_EXTERNAL'
  | 'VIS_CONFIDENTIAL'
  | 'VIS_RESTRICTED';

export type TimeSensitivity =
  | 'TS_EVERGREEN'
  | 'TS_TIME_SENSITIVE'
  | 'TS_DATED'
  | 'TS_URGENT';

// =============================================================================
// Pydantic Model Interfaces
// =============================================================================

/**
 * Individual facet classification result with confidence and alternatives.
 */
export interface ClassificationFacet {
  facet_name: string;
  facet_value: string;
  confidence: number; // 0.0-1.0
  alternatives?: string[];
}

/**
 * Complete 9-facet content classification result.
 * 
 * CRITICAL: All field names use snake_case to match Python API.
 * 
 * Includes:
 * - All 9 classification facets with individual confidence scores
 * - Freshness tracking (time_sensitivity, content_expiry, last_validated)
 * - Visibility settings (visibility)
 * - Versioning (version, supersedes_content_id, is_latest)
 * - Human review flags (requires_human_review, review_reasons)
 */
export interface ContentClassificationResult {
  // Core 9 facets with confidence scores
  format_type: FormatType;
  format_confidence: number; // 0.0-1.0
  
  purpose_type: PurposeType;
  purpose_confidence: number; // 0.0-1.0
  
  primary_audience_level: AudienceLevel;
  secondary_audience_level?: AudienceLevel | null;
  audience_level_confidence: number; // 0.0-1.0
  
  audience_domain: AudienceDomain;
  audience_domain_confidence: number; // 0.0-1.0
  
  industry_type: IndustryType;
  industry_confidence: number; // 0.0-1.0
  
  buyer_stage: BuyerStage; // Uses JourneyStage values from unified_stages.py
  buyer_stage_confidence: number; // 0.0-1.0
  
  technical_depth: TechnicalDepth;
  technical_depth_confidence: number; // 0.0-1.0
  
  entity_type: EntityType; // 9th facet - content attribution
  entity_id?: string | null; // FK to client/partner/vendor/solution
  entity_confidence: number; // 0.0-1.0
  
  subject_discipline: SubjectDiscipline;
  discipline_confidence: number; // 0.0-1.0
  
  // Freshness fields
  time_sensitivity: TimeSensitivity;
  content_expiry?: string | null; // ISO datetime string
  last_validated?: string | null; // ISO datetime string
  
  // Visibility
  visibility: ContentVisibility;
  
  // Versioning
  version: number; // Default: 1
  supersedes_content_id?: string | null;
  is_latest: boolean; // Default: true
  
  // Human review workflow
  requires_human_review: boolean; // Default: false
  review_reasons: string[]; // Default: []
  overall_confidence: number; // 0.0-1.0
}

/**
 * Human-readable labels for enum values
 * Used for dropdowns and display in UI
 */
export const FACET_LABELS: Record<string, string> = {
  // FormatType
  'FMT_WHITEPAPER': 'Whitepaper',
  'FMT_CASE_STUDY': 'Case Study',
  'FMT_DATASHEET': 'Datasheet',
  'FMT_BLOG': 'Blog Post',
  'FMT_EBOOK': 'eBook',
  'FMT_INFOGRAPHIC': 'Infographic',
  'FMT_VIDEO': 'Video',
  'FMT_WEBINAR': 'Webinar',
  'FMT_PODCAST': 'Podcast',
  'FMT_PRESENTATION': 'Presentation',
  'FMT_ONE_PAGER': 'One-Pager',
  'FMT_EMAIL': 'Email Template',
  'FMT_SOCIAL': 'Social Media Post',
  'FMT_LANDING_PAGE': 'Landing Page',
  'FMT_PROPOSAL': 'Proposal',
  'FMT_OTHER': 'Other',
  
  // PurposeType
  'PUR_EDUCATE': 'Educational Content',
  'PUR_PERSUADE': 'Persuasive/Sales Content',
  'PUR_ENABLE': 'Enablement/How-To',
  'PUR_COMPARE': 'Comparison/Evaluation',
  'PUR_VALIDATE': 'Validation/Proof',
  'PUR_ONBOARD': 'Onboarding/Training',
  'PUR_RETAIN': 'Retention/Customer Success',
  
  // AudienceLevel
  'AUD_EXECUTIVE': 'Executive (C-Suite)',
  'AUD_MANAGER': 'Manager/Director',
  'AUD_PRACTITIONER': 'Practitioner/Specialist',
  'AUD_TECHNICAL': 'Technical/IT Professional',
  'AUD_END_USER': 'End User',
  
  // AudienceDomain
  'DOM_IT': 'IT/Technology',
  'DOM_SECURITY': 'Security',
  'DOM_FINANCE': 'Finance',
  'DOM_MARKETING': 'Marketing',
  'DOM_SALES': 'Sales',
  'DOM_HR': 'Human Resources',
  'DOM_OPERATIONS': 'Operations',
  'DOM_LEGAL': 'Legal',
  'DOM_PROCUREMENT': 'Procurement',
  'DOM_ENGINEERING': 'Engineering',
  'DOM_PRODUCT': 'Product Management',
  'DOM_CUSTOMER_SUCCESS': 'Customer Success',
  'DOM_GENERAL': 'General Business',
  
  // IndustryType
  'IND_TECHNOLOGY': 'Technology',
  'IND_FINANCIAL': 'Financial Services',
  'IND_HEALTHCARE': 'Healthcare',
  'IND_MANUFACTURING': 'Manufacturing',
  'IND_RETAIL': 'Retail',
  'IND_GOVERNMENT': 'Government/Public Sector',
  'IND_EDUCATION': 'Education',
  'IND_MEDIA': 'Media & Entertainment',
  'IND_TELECOM': 'Telecommunications',
  'IND_ENERGY': 'Energy & Utilities',
  'IND_TRANSPORTATION': 'Transportation & Logistics',
  'IND_HOSPITALITY': 'Hospitality & Travel',
  'IND_REAL_ESTATE': 'Real Estate',
  'IND_NONPROFIT': 'Nonprofit',
  'IND_PROFESSIONAL_SERVICES': 'Professional Services',
  'IND_CROSS_INDUSTRY': 'Cross-Industry',
  
  // TechnicalDepth
  'TECH_EXECUTIVE': 'Executive Summary',
  'TECH_BUSINESS': 'Business Overview',
  'TECH_TECHNICAL': 'Technical Detail',
  'TECH_DEEP_DIVE': 'Deep Technical Dive',
  
  // EntityType
  'ENT_CLIENT': 'Client',
  'ENT_PARTNER': 'Partner',
  'ENT_VENDOR': 'Vendor',
  'ENT_SOLUTION': 'Solution',
  
  // SubjectDiscipline
  'DISC_CLOUD_INFRASTRUCTURE': 'Cloud Infrastructure',
  'DISC_CYBERSECURITY': 'Cybersecurity',
  'DISC_DATA_ANALYTICS': 'Data Analytics',
  'DISC_AI_ML': 'AI/Machine Learning',
  'DISC_DEVOPS': 'DevOps',
  'DISC_NETWORKING': 'Networking',
  'DISC_STORAGE': 'Storage',
  'DISC_VIRTUALIZATION': 'Virtualization',
  'DISC_IDENTITY_ACCESS': 'Identity & Access Management',
  'DISC_COMPLIANCE_GOVERNANCE': 'Compliance & Governance',
  'DISC_BUSINESS_CONTINUITY': 'Business Continuity',
  'DISC_DIGITAL_TRANSFORMATION': 'Digital Transformation',
  'DISC_CUSTOMER_EXPERIENCE': 'Customer Experience',
  'DISC_SALES_ENABLEMENT': 'Sales Enablement',
  'DISC_MARKETING_AUTOMATION': 'Marketing Automation',
  'DISC_FINANCIAL_MANAGEMENT': 'Financial Management',
  'DISC_HR_MANAGEMENT': 'HR Management',
  'DISC_SUPPLY_CHAIN': 'Supply Chain Management',
  'DISC_PROJECT_MANAGEMENT': 'Project Management',
  'DISC_COLLABORATION': 'Collaboration Tools',
  'DISC_MOBILE_SOLUTIONS': 'Mobile Solutions',
  'DISC_IOT': 'Internet of Things (IoT)',
  'DISC_GENERAL': 'General Business',
  
  // ContentVisibility
  'VIS_INTERNAL': 'Internal Only',
  'VIS_EXTERNAL': 'External/Public',
  'VIS_CONFIDENTIAL': 'Confidential',
  'VIS_RESTRICTED': 'Restricted Access',
  
  // TimeSensitivity
  'TS_EVERGREEN': 'Evergreen',
  'TS_TIME_SENSITIVE': 'Time-Sensitive',
  'TS_DATED': 'Dated',
  'TS_URGENT': 'Urgent',
};

/**
 * Get human-readable label for an enum value
 */
export function getFacetLabel(value: string): string {
  return FACET_LABELS[value] || value;
}

/**
 * Get all possible values for a facet type
 */
export function getFacetValues(facetType: string): Array<{ value: string; label: string }> {
  const typeMap: Record<string, readonly string[]> = {
    format_type: [
      'FMT_WHITEPAPER', 'FMT_CASE_STUDY', 'FMT_DATASHEET', 'FMT_BLOG', 
      'FMT_EBOOK', 'FMT_INFOGRAPHIC', 'FMT_VIDEO', 'FMT_WEBINAR', 
      'FMT_PODCAST', 'FMT_PRESENTATION', 'FMT_ONE_PAGER', 'FMT_EMAIL', 
      'FMT_SOCIAL', 'FMT_LANDING_PAGE', 'FMT_PROPOSAL', 'FMT_OTHER'
    ],
    purpose_type: [
      'PUR_EDUCATE', 'PUR_PERSUADE', 'PUR_ENABLE', 'PUR_COMPARE', 
      'PUR_VALIDATE', 'PUR_ONBOARD', 'PUR_RETAIN'
    ],
    primary_audience_level: [
      'AUD_EXECUTIVE', 'AUD_MANAGER', 'AUD_PRACTITIONER', 
      'AUD_TECHNICAL', 'AUD_END_USER'
    ],
    secondary_audience_level: [
      'AUD_EXECUTIVE', 'AUD_MANAGER', 'AUD_PRACTITIONER', 
      'AUD_TECHNICAL', 'AUD_END_USER'
    ],
    audience_domain: [
      'DOM_IT', 'DOM_SECURITY', 'DOM_FINANCE', 'DOM_MARKETING', 
      'DOM_SALES', 'DOM_HR', 'DOM_OPERATIONS', 'DOM_LEGAL', 
      'DOM_PROCUREMENT', 'DOM_ENGINEERING', 'DOM_PRODUCT', 
      'DOM_CUSTOMER_SUCCESS', 'DOM_GENERAL'
    ],
    industry_type: [
      'IND_TECHNOLOGY', 'IND_FINANCIAL', 'IND_HEALTHCARE', 'IND_MANUFACTURING', 
      'IND_RETAIL', 'IND_GOVERNMENT', 'IND_EDUCATION', 'IND_MEDIA', 
      'IND_TELECOM', 'IND_ENERGY', 'IND_TRANSPORTATION', 'IND_HOSPITALITY', 
      'IND_REAL_ESTATE', 'IND_NONPROFIT', 'IND_PROFESSIONAL_SERVICES', 'IND_CROSS_INDUSTRY'
    ],
    technical_depth: [
      'TECH_EXECUTIVE', 'TECH_BUSINESS', 'TECH_TECHNICAL', 'TECH_DEEP_DIVE'
    ],
    entity_type: [
      'ENT_CLIENT', 'ENT_PARTNER', 'ENT_VENDOR', 'ENT_SOLUTION'
    ],
    subject_discipline: [
      'DISC_CLOUD_INFRASTRUCTURE', 'DISC_CYBERSECURITY', 'DISC_DATA_ANALYTICS', 
      'DISC_AI_ML', 'DISC_DEVOPS', 'DISC_NETWORKING', 'DISC_STORAGE', 
      'DISC_VIRTUALIZATION', 'DISC_IDENTITY_ACCESS', 'DISC_COMPLIANCE_GOVERNANCE', 
      'DISC_BUSINESS_CONTINUITY', 'DISC_DIGITAL_TRANSFORMATION', 'DISC_CUSTOMER_EXPERIENCE', 
      'DISC_SALES_ENABLEMENT', 'DISC_MARKETING_AUTOMATION', 'DISC_FINANCIAL_MANAGEMENT', 
      'DISC_HR_MANAGEMENT', 'DISC_SUPPLY_CHAIN', 'DISC_PROJECT_MANAGEMENT', 
      'DISC_COLLABORATION', 'DISC_MOBILE_SOLUTIONS', 'DISC_IOT', 'DISC_GENERAL'
    ],
    time_sensitivity: [
      'TS_EVERGREEN', 'TS_TIME_SENSITIVE', 'TS_DATED', 'TS_URGENT'
    ],
    visibility: [
      'VIS_INTERNAL', 'VIS_EXTERNAL', 'VIS_CONFIDENTIAL', 'VIS_RESTRICTED'
    ],
  };

  const values = typeMap[facetType] || [];
  return values.map(value => ({
    value,
    label: getFacetLabel(value)
  }));
}
